import { filterDropdownOptions } from './filterDropdownOptions'

/**
 * Structural shapes kept local (rather than importing from `DropdownSelect.tsx`) so this helper does
 * not create an import cycle with the component that uses it — same reason as `filterDropdownOptions`.
 */
type SelectableOption = { key?: string; value?: any; label?: string; sublabel?: string }

export type SectionGroup<T extends SelectableOption> = {
  label: string
  options: T[]
  selectAllLabel?: string
  collapsible?: boolean
  hideLabel?: boolean
}

/**
 * A "section" group opts into the per-group behavior (its own select-all row, a collapsible header, or
 * no header). Plain groups keep the long-standing rendering: a header and every child.
 */
export function isSectionGroup<T extends SelectableOption>(group: SectionGroup<T>): boolean {
  return !!(group.selectAllLabel || group.collapsible || group.hideLabel)
}

/** A group's select-all row only earns its place when there is more than one option to select. */
export function showsGroupSelectAll<T extends SelectableOption>(group: SectionGroup<T>): boolean {
  return !!group.selectAllLabel && group.options.length > 1
}

/** Section groups narrow their own children by the search text; plain groups render every child. */
export function filterSectionGroupOptions<T extends SelectableOption>(
  group: SectionGroup<T>,
  searchFilter: string
): T[] {
  if (!isSectionGroup(group) || !searchFilter) {
    return group.options
  }
  return filterDropdownOptions({ options: group.options, searchFilter })
}

const sameOption = (a: SelectableOption, b: SelectableOption) => (a.key ? a.key === b.key : a.value === b.value)

export function isGroupFullySelected<T extends SelectableOption>(groupOptions: T[], value: T[]): boolean {
  return (
    groupOptions.length > 0 && groupOptions.every((option) => value.some((selected) => sameOption(selected, option)))
  )
}

/**
 * The group's select-all row: when every option in the group is selected, remove them all; otherwise
 * add the ones that are missing. Selections outside the group are kept.
 */
export function toggleGroupSelection<T extends SelectableOption>(value: T[], groupOptions: T[]): T[] {
  if (isGroupFullySelected(groupOptions, value)) {
    return value.filter((selected) => !groupOptions.some((option) => sameOption(selected, option)))
  }
  const missing = groupOptions.filter((option) => !value.some((selected) => sameOption(selected, option)))
  return [...value, ...missing]
}

/**
 * Orders selected values by where they sit in the flattened option list, so chips follow the menu's
 * group order (Active, Unassigned, Deactivated) instead of click order. Values not in the list keep
 * their relative order at the end.
 */
export function sortByOptionOrder<T extends SelectableOption>(value: T[], orderedOptions: T[]): T[] {
  const indexOf = (selected: T) => {
    const index = orderedOptions.findIndex((option) => sameOption(selected, option))
    return index === -1 ? Number.MAX_SAFE_INTEGER : index
  }
  return value
    .map((selected, position) => ({ selected, position, index: indexOf(selected) }))
    .sort((a, b) => a.index - b.index || a.position - b.position)
    .map(({ selected }) => selected)
}

/**
 * A collapsible group starts collapsed. It opens when the user toggles it, or while a search matches
 * one of its children so the match is not hidden.
 */
export function isGroupExpanded({
  collapsible,
  toggledOpen,
  searchFilter,
  matchCount
}: {
  collapsible?: boolean
  toggledOpen?: boolean
  searchFilter?: string
  matchCount: number
}): boolean {
  if (!collapsible) {
    return true
  }
  return !!toggledOpen || (!!searchFilter && matchCount > 0)
}
